import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@styles/index.css';
import { RouterProvider } from 'react-router-dom';
import { AppProvider } from './context/AppProvider.jsx';
import './i18n';

// Dev-only: `npm run dev:mock` (Vite mode "mock") serves the admin API from
// in-memory fixtures so the UI is fully populated with no backend.
//
// The router is imported AFTER the mocks install: createBrowserRouter starts
// executing route loaders the moment the router module is evaluated, so the
// mocked axios adapter must already be in place by then. Production builds
// never load the mock module.
async function boot() {
  if (import.meta.env.MODE === 'mock') {
    const { installMocks } = await import('./mocks/install.js');
    installMocks();
  }
  // White-label theming + embed session must be settled BEFORE the router imports in embed mode,
  // because route loaders run on router module evaluation. B2C/standalone stays non-blocking.
  const { useBrandingStore } = await import('./stores/useBranding.store.js');
  const { isEmbedded } = await import('./lib/embed.js');

  if (isEmbedded()) {
    await useBrandingStore.getState().loadBranding();
    const { frameAncestors } = useBrandingStore.getState();
    const { bootstrapEmbedSession } = await import('./lib/embedSession.js');
    await bootstrapEmbedSession(frameAncestors);
  } else {
    // Non-blocking: CSS defaults are already Zermit, so B2C renders instantly and unchanged.
    useBrandingStore.getState().loadBranding();
  }

  const { default: router } = await import('./router/index.jsx');

  createRoot(document.getElementById('root')).render(
    <AppProvider>
      <RouterProvider router={router} />
    </AppProvider>
  );
}

boot();
